import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Divider, Grid, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import { BreadCrumbsAndTitle } from 'components/generics/BreadCrumbsAndTitle';
import { AlertsInformativos } from 'components/generics/AlertsInformativos';
import { alertInformativo } from 'commons/texts';
import { mtDividerUpTable } from 'commons';
import { CalendarPlanner, DemandItem } from './components/CalendarPlanner';
import { PlannerFormSidebar } from './components/PlannerFormSidebar';
import { PlannerSummary } from './components/PlannerSummary';
import { Formik, Form } from 'formik';
import { formInitialValues } from './formik/planner/formInitialValues';
import { validationSchema } from './formik/planner/validationSchema';
import moment from 'moment';
import SaveIcon from '@mui/icons-material/Save';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    getDataRequerimientoPersonalCreateStore,
    savePlanningShiftRequestStore
} from 'store/shift_demands/thunks';
import { useResponseHandler } from 'hooks/useResponseHandler';

export const CreateRequerimientoPersonal = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const [demands, setDemands] = useState<DemandItem[]>([]);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const { ResponseComponents, handleResponse, setMsgSnack, setAlertSnack } = useResponseHandler();

    useEffect(() => {
        // Load options for Cargo and Turnos
        // dispatch(getOnlyCargosSelectStore());
        // dispatch(storeGetShiftTemplatesForSelect());
        dispatch( getDataRequerimientoPersonalCreateStore() )
    }, [dispatch]);

    const handleDateSelect = (start: Date, end: Date) => {
        // FullCalendar's end date is exclusive, so we generate the inclusive array of dates
        const dates: string[] = [];
        let curr = moment.utc(start);
        const last = moment.utc(end).subtract(1, 'days');
        while (curr.isSameOrBefore(last)) {
            dates.push(curr.format('YYYY-MM-DD'));
            curr.add(1, 'days');
        }
        setSelectedDates(dates);
    };

    const handleDateClick = (date: Date) => {
        const dateStr = moment.utc(date).format('YYYY-MM-DD');
        setSelectedDates([dateStr]);
    };

    const handleEventClick = (demand: DemandItem) => {
        console.log('Event clicked:', demand);
    };


    const getDatesBasedOnRepetition = (repeticion: string, baseDates: string[], customDays: string[]) => {
        if (repeticion === 'selected') {
            return baseDates;
        }
        if (baseDates.length === 0) return [];
        
        const first = moment(baseDates[0]);
        const last = moment(baseDates[baseDates.length - 1]);
        
        let result: string[] = [];
        let curr = first.clone();
        
        while (curr.isSameOrBefore(last)) {
            const dateStr = curr.format('YYYY-MM-DD');
            const dayOfWeek = curr.day().toString(); // 0 (Sun) to 6 (Sat)
            
            if (repeticion === 'range') {
                result.push(dateStr);
            } else if (repeticion === 'weekdays') {
                if (dayOfWeek !== '0' && dayOfWeek !== '6') result.push(dateStr);
            } else if (repeticion === 'weekends') {
                if (dayOfWeek === '0' || dayOfWeek === '6') result.push(dateStr);
            } else if (repeticion === 'custom') {
                if (customDays.includes(dayOfWeek)) result.push(dateStr);
            }
            curr.add(1, 'days');
        }
        return result;
    };

    const handleApply = (values: any) => {
        if (selectedDates.length === 0) {
            toast.warning('Por favor seleccione al menos un día en el calendario.');
            return;
        }

        const cargoObj = values.cargo; 
        const turnosArr = values.turnos;
        const targetDates = getDatesBasedOnRepetition(values.repeticion, selectedDates, values.diasSemana);

        if (targetDates.length === 0) {
            toast.warning('La configuración de repetición no abarca ningún día en el rango seleccionado.');
            return;
        }

        const newDemands: DemandItem[] = [...demands];
        
        // Colores base para diferenciar cargos (ejemplo estático, en producción podría venir del cargo)
        const colors = ['#696CFF', '#71DD37', '#03C3EC', '#FFAB00', '#FF3E1D'];
        const color = colors[cargoObj.value?.length % colors.length] || '#696CFF';

        targetDates.forEach(dateStr => {
            turnosArr.forEach((turno: any) => {
                // Find if there's already a demand for this Date + Cargo + Turno
                const existingIdx = newDemands.findIndex(d => d.dateStr === dateStr && d.cargoId === cargoObj.value && d.turnoId === turno.value);
                
                const demandObj: DemandItem = {
                    id: existingIdx >= 0 ? newDemands[existingIdx].id : Math.random().toString(36).substr(2, 9),
                    dateStr,
                    cargoId: cargoObj.value,
                    cargoName: cargoObj.label,
                    turnoId: turno.value,
                    turnoName: turno.label,
                    cantidad: Number(values.cantidad),
                    color
                };

                if (existingIdx >= 0) {
                    newDemands[existingIdx] = demandObj; // Overwrite
                } else {
                    newDemands.push(demandObj);
                }
            });
        });

        setDemands(newDemands);
        toast.success(`Se actualizaron las demandas para ${targetDates.length} día(s).`);
    };

    const handleSave = async () => {
        if (demands.length === 0) {
            toast.warning('No hay demandas para guardar.');
            return;
        }

        // Obtener startDate y endDate
        const sortedDates = [...demands].sort((a, b) => a.dateStr.localeCompare(b.dateStr));
        const startDate = sortedDates[0].dateStr;
        const endDate = sortedDates[sortedDates.length - 1].dateStr;

        // Generar un nombre representativo
        const uniqueCargos = Array.from(new Set(demands.map(d => d.cargoName))).join(', ');
        const name = `${uniqueCargos} | ${startDate} al ${endDate}`;

        const planningShiftRequestsDetail = demands.map(d => ({
            date: `${d.dateStr}T12:00:00.000Z`,
            position: d.cargoId,
            shift: d.turnoId,
            requiredEmployees: d.cantidad,
            color: d.color
        }));

        const payload = {
            name,
            startDate,
            endDate,
            planningShiftRequestsDetail
        };

        const response = await dispatch(savePlanningShiftRequestStore(payload));
        await handleResponse(response, 'Demanda guardada correctamente.', true);
        
    };

    return (
        <Box sx={{ pb: 5, width: '100%', p: 1 }}>
            {ResponseComponents}
            <Box sx={{ mb: 3 }}>
                <BreadCrumbsAndTitle 
                    title="Planificador Visual de Demanda de Personal" 
                    mainModule="Programador Inteligente"
                    parent="Requerimiento Personal"
                    action="/talento-humano/programador/requerimiento-de-personal"
                    icon="AutoAwesome"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.generadorTurnos} /> 
                </Typography>
            </Box>

            <Divider sx={{ marginBottom: mtDividerUpTable }}/>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Planificación Mensual
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Selecciona los días en el calendario para registrar la demanda por cargo y turno.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={demands.length === 0}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Guardar Planificación
                </Button>
            </Box>
            
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 9 }}>
                    <Paper elevation={0} sx={{ p: 2, border: '1px solid #E0E3E7', borderRadius: 2, height: '100%' }}>
                        <CalendarPlanner 
                            demands={demands} 
                            onDateSelect={handleDateSelect}
                            onDateClick={handleDateClick}
                            onEventClick={handleEventClick}
                        />
                    </Paper>
                </Grid>
                
                <Grid size={{ xs: 12, md: 3 }}>
                    {selectedDates.length > 0 ? (
                        <Formik
                            initialValues={formInitialValues}
                            validationSchema={validationSchema}
                            onSubmit={() => {}}
                        >
                            {() => (
                                <Form style={{ height: '100%' }}>
                                    <PlannerFormSidebar 
                                        onApply={handleApply} 
                                        selectedDates={selectedDates} 
                                    />
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <Paper elevation={0} sx={{ p: 2, border: '1px dashed #E0E3E7', borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                Selecciona uno o varios días en el calendario para asignar demanda.
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            <PlannerSummary demands={demands} />

        </Box>
    );
};
